import { useEffect, useState } from "react"
import ChatTextInput from "../commons/ChatTextInput"
import { useAtom } from "jotai"
import { recipientIdAtom, showIndividualMessageBoxAtom, showMessageBoxAtom, userAtom } from "../../store"
import { addRecipient, getUserData } from "../../firebase/users"
import { addDoc, collection, onSnapshot, orderBy, query, where } from "firebase/firestore"
import { db } from "../../firebase/config"
import { Link } from "react-router-dom"
import { setMessageRead, listenToUnreadMessageCount } from "../../firebase/messages"
import { useAuth } from "../../context/authContext"

const isFirebaseImageUrl = (text) =>
  typeof text === "string" && text.includes("https://firebasestorage.googleapis.com");

const MessageBody = ({ text, onImageClick }) => {
  const [errored, setErrored] = useState(false);
  if (isFirebaseImageUrl(text) && !errored) {
    return (
      <img
        src={text}
        alt="attachment"
        className="max-w-xs md:max-w-sm rounded cursor-zoom-in"
        onError={() => setErrored(true)}
        onClick={() => onImageClick?.(text)}
      />
    );
  }
  return <p className="leading-[1.5em] break-words">{text}</p>;
};

// Timestamp → display string (uses Firestore Timestamp if present)
const tsToDisplay = (ts) => {
  let d;
  if (!ts) return "";
  if (typeof ts.toDate === "function") {
    d = ts.toDate();
  } else if (typeof ts.seconds === "number") {
    d = new Date(ts.seconds * 1000);
  } else if (ts instanceof Date) {
    d = ts;
  } else {
    return "";
  }
  const time = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "numeric", hour12: true });
  const date = `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
  return `${time} ${date}`;
};

const SidebarIndividualChat = () => {
  const [messages, setMessages] = useState([]);
  const [sentMessages, setSentMessages] = useState([]);
  const [receivedMessages, setReceivedMessages] = useState([]);
  const [userData] = useAtom(userAtom);
  const [messageText, setMessageText] = useState("");
  const [showIndividualMessageBox, setShowIndividualMessageBox] = useAtom(showIndividualMessageBoxAtom);
  const [, setShowMessageBox] = useAtom(showMessageBoxAtom);
  const [recipientId] = useAtom(recipientIdAtom);
  const [partyData, setPartyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [, setRealTimeUnreadMessages] = useState([]);
  const { getUpdatedUserData, user } = useAuth();

  // Lightbox state
  const [openImageUrl, setOpenImageUrl] = useState(null);
  const handleImageOpen = (url) => setOpenImageUrl(url);
  const handleImageClose = () => setOpenImageUrl(null);

  // Lock scroll + ESC close for lightbox
  useEffect(() => {
    if (!openImageUrl) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") handleImageClose(); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [openImageUrl]);

  useEffect(() => {
    if (recipientId !== null) {
      getUserData(recipientId).then((data) => {
        setPartyData(data);
        setLoading(false);
      });
    }
    // Mark messages as read when opening individual chat
    if (userData && userData.unReadMessages) {
      userData.unReadMessages
        .filter((item) => item.senderId === recipientId)
        .forEach(async (msg) => { await setMessageRead(msg.id); });
      getUpdatedUserData();
    }
  }, [recipientId]); 

  // Real-time unread listener
  useEffect(() => {
    if (user && user.uid && recipientId && showIndividualMessageBox) {
      const unsubscribe = listenToUnreadMessageCount(user.uid, (msgs) => {
        setRealTimeUnreadMessages(msgs);
        const fromRecipient = msgs.filter((m) => (m.senderId || m.sender) === recipientId);
        fromRecipient.forEach(async (m) => { await setMessageRead(m.id); });
      });
      return () => { if (typeof unsubscribe === "function") unsubscribe(); };
    }
  }, [user, recipientId, showIndividualMessageBox]);

  // Merge + sort threads
  useEffect(() => {
    const combined = [...sentMessages, ...receivedMessages];
    combined.sort((a, b) => {
      const at = a?.timestamp?.seconds * 1e9 + (a?.timestamp?.nanoseconds || 0);
      const bt = b?.timestamp?.seconds * 1e9 + (b?.timestamp?.nanoseconds || 0);
      return (at || 0) - (bt || 0);
    });
    setMessages(combined);
  }, [sentMessages, receivedMessages]);

  // Firestore listeners
  useEffect(() => {
    if (!(userData && userData.id)) return;

    setMessages([]);

    const q1 = query(
      collection(db, "messages"),
      where("recipientId", "==", recipientId),
      where("senderId", "==", userData.id),
      orderBy("timestamp")
    );

    const q2 = query(
      collection(db, "messages"),
      where("recipientId", "==", userData.id),
      where("senderId", "==", recipientId),
      orderBy("timestamp")
    );

    const unsubscribe1 = onSnapshot(q1, (snapshot) => {
      setSentMessages(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
    });

    const unsubscribe2 = onSnapshot(q2, (snapshot) => {
      setReceivedMessages(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
    });

    return () => {
      unsubscribe1();
      unsubscribe2();
    };
  }, [recipientId, userData]); 

  const onChangeMessageText = (value) => setMessageText(value);

  const onKeyDown = (event) => {
    if (event.key === "Enter") sendMessage();
  };

  const sendMessage = async () => {
    if (messageText === "") return;
    const text = messageText;
    setMessageText("");
    await addDoc(collection(db, "messages"), {
      text,
      senderId: userData.id,
      recipientId,
      timestamp: new Date(),
      state: "unread",
    });
    if (!userData.recipients || userData.recipients.findIndex((i) => i.id === recipientId) === -1) {
      await addRecipient(userData.id, recipientId);
      await addRecipient(recipientId, userData.id);
      await getUpdatedUserData();
    }
  };

  const sendImage = async (imageURL) => {
    if (!imageURL) return;
    setMessageText("");
    await addDoc(collection(db, "messages"), {
      text: imageURL,
      senderId: userData.id,
      recipientId,
      timestamp: new Date(),
      state: "unread",
    });
    if (!userData.recipients || userData.recipients.findIndex((i) => i.id === recipientId) === -1) {
      await addRecipient(userData.id, recipientId);
      await addRecipient(recipientId, userData.id);
      await getUpdatedUserData();
    }
  };

  useEffect(() => {
    const el = document.querySelector("#message-box");
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);

  return (
    showIndividualMessageBox && (
      <>
        {!loading && (
          <div className="fixed right-0 z-[9999] bg-[#181818] md:w-[726px] w-full h-full md:px-6 py-8 max-md:top-[69px] border-l-[1px] border-gray">
            <div className="flex justify-between max-md:hidden">
              <div className="flex max-md:px-4">
                <img src="/assets/icons/icon-logo.svg" alt="user" className="mr-3" />
                <span className="uppercase text-white text-nowrap">Your Chats</span>
              </div>

              <button onClick={() => setShowIndividualMessageBox(false)}>
                <img src="/assets/icons/icon-close-white.svg" alt="close" />
              </button>
            </div>

            <hr className="border-gray my-6 max-md:hidden" />

            <div
              className="flex justify-between max-md:px-4"
              onClick={() => {
                setShowIndividualMessageBox(false);
                setShowMessageBox(true);
              }}
            >
              <button className="flex items-center mb-6 text-white w-fit">
                <svg width="4" height="8" viewBox="0 0 4 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M0.187825 3.14583L3.20866 0.125C3.25033 0.0833333 3.29546 0.0520833 3.34408 0.03125C3.39269 0.0104166 3.44477 -2.06415e-08 3.50033 -1.82131e-08C3.61144 -1.33563e-08 3.70866 0.0381944 3.79199 0.114583C3.87533 0.190972 3.91699 0.291667 3.91699 0.416667L3.91699 6.75C3.91699 6.875 3.87533 6.97569 3.79199 7.05208C3.70866 7.12847 3.61144 7.16667 3.50033 7.16667C3.47255 7.16667 3.37533 7.125 3.20866 7.04167L0.187825 4.02083C0.11838 3.95139 0.0697692 3.88194 0.0419913 3.8125C0.0142136 3.74306 0.000325043 3.66667 0.000325046 3.58333C0.00032505 3.5 0.0142137 3.42361 0.0419913 3.35417C0.0697692 3.28472 0.11838 3.21528 0.187825 3.14583Z" fill="#1AB6F9" />
                </svg>
                <span className="ml-2">Go Back</span>
              </button>

              <button className="md:hidden" onClick={() => setShowIndividualMessageBox(false)}>
                <img src="/assets/icons/icon-close-white.svg" alt="close" />
              </button>
            </div>

            <div className="flex mb-4 border-style-decoration max-md:mx-2 justify-between border-gray border-[1px] px-6 py-2 bg-[#222] text-white text-sm">
              <div className="flex items-center">
                <img src="/assets/avatars/default.png" className="w-10 h-10 mr-3" alt="user" />
                {partyData?.displayName}
              </div>

              {partyData?.id && (
                <Link to={`/user-profile/${partyData.id}`} onClick={() => setShowIndividualMessageBox(false)}>
                  <button className="border-style-decoration px-8 py-3 hover:bg-white hover:text-black transition">View Profile</button>
                </Link>
              )}
            </div>

            <hr className="md:hidden border-gray mx-8" />

            <div className="border-gray-100 md:border-[1px] md:px-6 px-2 pb-6 pt-1">
              <div className="overflow-auto mb-4" id="message-box">
                {messages.map((msg) =>
                  msg.senderId === userData.id ? (
                    <div className="flex justify-end mt-7" key={msg.id}>
                      <div className="mr-7">
                        <div className="bg-[#292929] border-gray border-[1px] w-full text-white text-sm px-4 py-4">
                          <div className="text-[#7F8289] mb-2">You</div>
                          <MessageBody text={msg.text} onImageClick={handleImageOpen} />
                        </div>
                        <div className="text-end text-gray-100 text-xs mt-1">{tsToDisplay(msg.timestamp)}</div>
                      </div>
                      <img src="/assets/avatars/default.png" className="w-12 h-12 mr-3" alt="user" />
                    </div>
                  ) : (
                    <div className="flex mt-7" key={msg.id}>
                      <img src="/assets/avatars/default.png" className="w-12 h-12 mr-7" alt="user" />
                      <div>
                        <div className="bg-[#292929] border-gray border-[1px] w-full text-white text-sm px-4 py-4">
                          <div className="text-[#7F8289] mb-2">{partyData?.displayName}</div>
                          <MessageBody text={msg.text} onImageClick={handleImageOpen} />
                        </div>
                        <div className="text-gray-100 text-xs mt-1">{tsToDisplay(msg.timestamp)}</div>
                      </div>
                    </div>
                  )
                )}
              </div>

              <ChatTextInput
                divClassName="bg-white/5 backdrop-blur-sm !rounded-none !bg-transparent group focus-within:border-[#1AB6F9]"
                placeholder="Type here..."
                inputClassName="placeholder:text-white/60"
                onChange={onChangeMessageText}
                onKeyDown={onKeyDown}
                value={messageText}
                onClick={sendMessage}
                sendImage={sendImage}
              />
            </div>
          </div>
        )}

        {/* Lightbox overlay */}
        {openImageUrl && (
          <div
            className="fixed inset-0 z-[10000] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={handleImageClose}
            role="dialog"
            aria-modal="true"
          >
            <div className="relative max-w-[95vw] max-h-[95vh]" onClick={(e) => e.stopPropagation()}>
              <button
                onClick={handleImageClose}
                className="absolute -top-3 -right-3 md:top-0 md:right-0 translate-y-[-100%] md:translate-y-0 bg-white text-black rounded-full p-2 shadow"
                aria-label="Close"
                title="Close"
              >
                ✕
              </button>

              <img
                src={openImageUrl}
                alt="preview"
                className="max-w-[95vw] max-h-[85vh] object-contain rounded-lg shadow-xl"
                draggable={false}
              />
            </div>
          </div>
        )}

        {/* Backdrop behind the side panel */}
        <div
          className="fixed max-md:hidden inset-0 z-[9998] bg-[#000] backdrop-blur-sm bg-opacity-75 transition-opacity"
          onClick={() => setShowIndividualMessageBox(false)}
        />
      </>
    )
  );
};

export default SidebarIndividualChat;
